import { Box, Text, TextProps, keyframes } from '@chakra-ui/react';
import { useLayoutEffect, useRef, useState } from 'react';

interface ScrollingTextProps extends Omit<TextProps, 'children'> {
  children: string;
  speed?: number;
}

const ScrollingText = ({ children, speed = 25, ...props }: ScrollingTextProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [distance, setDistance] = useState(0);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const text = textRef.current;
    if (!viewport || !text) return;

    const measure = () => {
      const width = text.offsetWidth;
      setDistance(width > viewport.clientWidth ? width + 24 : 0);
    };
    measure();
    // Font loading, content changes, and resizing can each change overflow.
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(text);
    return () => observer.disconnect();
  }, [children]);

  const scroll = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(-${distance}px); }
  `;

  return (
    <Text
      {...props}
      as="div"
      ref={viewportRef}
      w="100%"
      minW={0}
      overflow="hidden"
      whiteSpace="nowrap"
      data-scrolling={distance > 0}
    >
      <Box
        key={children}
        display="flex"
        w="max-content"
        gap="24px"
        animation={distance ? `${scroll} ${distance / speed}s linear infinite` : 'none'}
      >
        <Box ref={textRef} flexShrink={0}>
          {children}
        </Box>
        {distance > 0 && (
          <Box flexShrink={0} aria-hidden="true">
            {children}
          </Box>
        )}
      </Box>
    </Text>
  );
};

export default ScrollingText;
